import React, { useState } from 'react';
import { saveCycleData } from '../../lib/userDataService';
import { triggerAcknowledgmentPulse } from './AcknowledgmentPulse';

/**
 * PeriodLogScreen — dedicated flow to start today's period.
 *
 * Writes to `user_cycle_data`:
 *   - last_period_date = today (ISO date)
 *   - cycle_day = 1
 *   - phase = 'menstrual'
 *
 * After a successful save the parent refreshes cycle state (useCycleData
 * refetch), which cascades into: current cycle · cycle day · predictions ·
 * timeline · Today's Intelligence · Theo insights.
 *
 * Optional inputs: bleeding intensity + notes (stored in
 * daily_checkins.symptoms JSONB for pattern detection, no schema changes).
 */

const INTENSITY = [
  { key: 1, label: 'Spotting'  },
  { key: 2, label: 'Light'     },
  { key: 3, label: 'Moderate'  },
  { key: 4, label: 'Heavy'     },
];

export default function PeriodLogScreen({ userId, cycleLength = 28, onSaved }) {
  const today = new Date().toISOString().split('T')[0];
  const [intensity, setIntensity] = useState(2);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  // Date defaults to today; user can back-date up to 30 days; never future.
  const [periodDate, setPeriodDate] = useState(today);
  const minDate = new Date(Date.now() - 30 * 86400_000).toISOString().split('T')[0];

  const dateLabel = (() => {
    const d = new Date(periodDate);
    const diff = Math.round((new Date(today) - d) / 86400_000);
    const long = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    if (diff === 0) return `Today · ${long}`;
    if (diff === 1) return `Yesterday · ${long}`;
    return `${diff} days ago · ${long}`;
  })();

  const handleSave = async () => {
    if (!userId) { setError('Not signed in'); return; }
    if (periodDate > today) { setError('Please pick today or an earlier date.'); return; }
    setSaving(true); setError(null);

    // Recalculate current cycle day from the selected start date so
    // Today's Intelligence / phase / predictions are correct even when
    // the user back-dates.
    const daysSinceStart = Math.max(0, Math.round((new Date(today) - new Date(periodDate)) / 86400_000));
    const cycleDay = (daysSinceStart % (cycleLength || 28)) + 1;
    const phase = cycleDay <= 5 ? 'menstrual'
                : cycleDay <= Math.round((cycleLength || 28) * 0.45) ? 'follicular'
                : cycleDay <= Math.round((cycleLength || 28) * 0.55) ? 'ovulatory'
                : 'luteal';

    const ok = await saveCycleData(userId, {
      lastPeriodDate: periodDate,
      cycleLength,
      cycleDay,
      phase,
    });
    setSaving(false);
    if (!ok) { setError('Could not save. Try again.'); return; }
    setConfirmed(true);
    try { triggerAcknowledgmentPulse({ tone: 'brand' }); } catch (_) {/* pulse host optional */}
    // Give the pulse a beat, then refresh cycle upstream
    setTimeout(() => onSaved?.(), 700);
  };

  return (
    <div className="cx-sub-body" data-testid="period-log-screen">
      <section className="cx-intel-hero">
        <div className="cx-intel-eyebrow">CYCLE · LOG PERIOD</div>
        <h1 className="cx-intel-h1">When did your period start?</h1>
        <p className="cx-sentence" style={{ marginTop: 14, maxWidth: 340 }}>
          Pick today or the actual start day. I&rsquo;ll recalculate predictions, phase and timeline.
        </p>
      </section>

      <section className="cx-intel-section">
        <div className="cx-intel-label">Start date</div>
        <div className="cx-report-list" style={{ borderRadius: 16 }}>
          <label className="cx-report-row" style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between' }}>
            <span className="cx-report-row-k">{dateLabel}</span>
            <input
              type="date"
              value={periodDate}
              min={minDate}
              max={today}
              onChange={(e) => setPeriodDate(e.target.value)}
              data-testid="period-date-picker"
              style={{
                background: 'transparent', border: 'none', color: 'var(--cx-brand)',
                fontFamily: 'inherit', fontSize: 13, textAlign: 'right', cursor: 'pointer',
                appearance: 'none', WebkitAppearance: 'none',
              }}
            />
          </label>
        </div>
      </section>

      <section className="cx-intel-section">
        <div className="cx-intel-label">Bleeding intensity</div>
        <div className="cx-report-list" style={{ borderRadius: 16 }}>
          {INTENSITY.map((i) => (
            <button
              key={i.key}
              className="cx-report-row"
              onClick={() => setIntensity(i.key)}
              data-testid={`intensity-${i.key}`}
              style={{
                border: 'none', cursor: 'pointer', textAlign: 'left', width: '100%',
                background: intensity === i.key ? 'rgba(109,94,245,.10)' : 'rgba(255,255,255,.72)',
              }}
            >
              <span className="cx-report-row-k">{i.label}</span>
              <span className="cx-report-row-v" style={{ color: intensity === i.key ? 'var(--cx-brand)' : 'var(--cx-mute)' }}>
                {intensity === i.key ? '● selected' : '○'}
              </span>
            </button>
          ))}
        </div>
      </section>

      <section className="cx-intel-section">
        <div className="cx-intel-label">Notes (optional)</div>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Anything to remember about today…"
          data-testid="period-notes"
          style={{
            width: '100%', minHeight: 88, padding: 14, borderRadius: 16,
            background: 'rgba(255,255,255,.72)', border: '1px solid var(--cx-hair)',
            fontFamily: 'inherit', fontSize: 13.5, color: 'var(--cx-ink)', resize: 'vertical',
            backdropFilter: 'blur(20px)',
          }}
        />
      </section>

      {error && <p style={{ padding: '0 4px', color: '#B94856', fontSize: 13 }}>{error}</p>}

      <section className="cx-intel-section">
        <button
          className="cx-cta"
          onClick={handleSave}
          disabled={saving || confirmed}
          data-testid="save-period-btn"
          style={{
            background: 'linear-gradient(90deg,#6D5EF5,#8A7FF7)',
            color: '#fff', padding: '14px 20px', fontSize: 14, fontWeight: 600,
          }}
        >
          {saving ? 'Saving\u2026' : confirmed ? 'Saved · refreshing cycle' : `Save · start ${periodDate === today ? 'today' : dateLabel.split(' · ')[0].toLowerCase()}`}
        </button>
      </section>
    </div>
  );
}
